import { useContext, useEffect, useMemo, useState } from 'react';
import { InterruptionAllowedCoverSelector } from '../../../../common/components/selectors/interruption/InterruptionAllowedCoverSelector';
import { WorkshiftsLongShiftSelector } from '../../../../common/components/selectors/Workshifts/WorkshiftsLongShiftSelector';
import { CoverRequest } from '../../../../models/coverings/CoverRequest';
import { SchedulingByEmployeeAndDateModel } from '../../../../models/coverings/workshiftSchedulingCover';
import { SchedulingChangeRequestsRequest } from '../../../../models/employees-portal/CreateSchedulingRequest';
import { CoveringsAPI } from '../../../../services/external/APIs/coverings/Coverings.api';
import { TProps } from '../../home/HomeEmployeesPortal';
import { CoverSchedulingByEmployeeAndDate } from '../../models/coverScheduling';
import { CoverPersonSearchField } from '../cover-person-search-field/CoverPersonSearchField';
import { AppContext, FormBlock } from '../../../../common';

const COVER_CONTROL_WITH_RESTRICTIONS_BY_SHIFT = 'CoverControlWithRestrictionsByShift';

const parseApplicationParameterBoolean = (value: string | number | null | undefined): boolean => {
  if (value == null) {
    return false;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
};

interface Props {
  coverRequest: CoverRequest;
  coverScheduling: CoverSchedulingByEmployeeAndDate | undefined;
  request: SchedulingChangeRequestsRequest;
  requestModalInfo: TProps | undefined;
  errorMessage: string;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setCoverRequest: React.Dispatch<React.SetStateAction<CoverRequest>>;
  handlerInputChangeCover: (name: string, value: string | null | number | undefined) => void;
  handleInputInterruption: (name: string, value: any) => void;
  setIsRequireLongShift: (name: boolean) => void;
  isRequireLongShift: boolean;
  handlerChangeCoverPerson: (model: { id: number; fullName: string }) => void;
}

export const RequestCoveringsFormModal = ({
  coverRequest,
  coverScheduling,
  request,
  requestModalInfo,
  handlerChangeCoverPerson,
  handlerInputChangeCover,
  setCoverRequest,
  handleInputInterruption,
  errorMessage,
  setErrorMessage,
  isRequireLongShift,
  setIsRequireLongShift,
}: Props) => {
  const { parametersApplication } = useContext(AppContext);

  const coverControlWithRestrictionsByShift = useMemo(() => {
    const parameter = parametersApplication.find(
      (p) => p.name === COVER_CONTROL_WITH_RESTRICTIONS_BY_SHIFT,
    );

    return parseApplicationParameterBoolean(parameter?.value);
  }, [parametersApplication]);

  const isCompensationDateDisabled =
    coverControlWithRestrictionsByShift &&
    coverScheduling?.workshiftId != null &&
    coverRequest.interruptionTypeRequestId == null;

  const [coverEmployeeWorkshift, setCoverEmployeeWorkshift] =
    useState<SchedulingByEmployeeAndDateModel>();
  const [employeeWorkshift, setEmployeeWorkshift] = useState<SchedulingByEmployeeAndDateModel>();
  const [employee, setEmployees] = useState<{ id: number; fullName: string } | undefined>();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (coverRequest.compensationDate && requestModalInfo?.info?.personId && employee?.id) {
      setIsLoading(true);
      CoveringsAPI.getWorkshiftShcedulingByEmployee({
        date: coverRequest.compensationDate,
        employeeId: requestModalInfo?.info?.personId,
      })
        .then((res: SchedulingByEmployeeAndDateModel) => {
          setEmployeeWorkshift(res);
          setCoverRequest((prev) => ({
            ...prev,
            requesterCompensationWorkshiftId: res.workshiftId,
            requesterCompensationInterruptionTypeId: res.interruptionTypeId,
          }));
        })
        .finally(() => {});

      CoveringsAPI.getWorkshiftShcedulingByEmployee({
        date: coverRequest.compensationDate,
        employeeId: employee?.id,
      })
        .then((res) => {
          setCoverEmployeeWorkshift(res);
          setCoverRequest((prev) => ({
            ...prev,
            coverCompensationWorkshiftId: res.workshiftId,
            coverCompensationInterruptionTypeId: res.interruptionTypeId,
          }));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [coverRequest.compensationDate, requestModalInfo?.info?.personId, employee?.id]);

  useEffect(() => {
    if (!employee) {
      handlerInputChangeCover('workshiftRequestId', null);

      setEmployeeWorkshift(undefined);
      setEmployees(undefined);
    }
  }, [employee]);

  useEffect(() => {
    if (!coverRequest.interruptionTypeRequestId) {
      setCoverRequest((prev) => ({
        ...prev,
        coverCompensationInterruptionTypeId: null,
        coverCompensationWorkshiftId: null,
        requesterCompensationWorkshiftId: null,
        requesterCompensationInterruptionTypeId: null,
        workshiftIdToBeCompensated: null,
        compensationDate: null,
      }));
    }
  }, [coverRequest.interruptionTypeRequestId]);

  const getWorkshiftDescription = (data: SchedulingByEmployeeAndDateModel) => {
    if (data.interruptionTypeId) {
      return data.interruptionTypeName;
    }
    if (data.workshiftId) {
      return data.workshiftDescription;
    }
    return 'Sin programación';
  };

  const disableWorkshiftsLongShiftSelectorCase1 = () => {
    const caso1 =
      coverScheduling?.workshiftId &&
      employeeWorkshift?.workshiftId &&
      coverEmployeeWorkshift?.workshiftId;

    if (caso1 && coverRequest.compensationDate != null) {
      return false;
    }

    return true;
  };

  const disableWorkshiftsLongShiftSelectorCase2 = () => {
    const caso2 =
      requestModalInfo?.info?.workshiftId &&
      coverScheduling?.workshiftId &&
      coverEmployeeWorkshift?.workshiftId;

    if (caso2 && coverRequest.compensationDate != null) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    let isDisable = disableWorkshiftsLongShiftSelectorCase1();
    let isDisable2 = disableWorkshiftsLongShiftSelectorCase2();

    if (!isDisable || !isDisable2) {
      setIsRequireLongShift(true);
    } else {
      setIsRequireLongShift(false);
    }
  }, [
    coverEmployeeWorkshift,
    employeeWorkshift,
    coverRequest,
    coverScheduling,
    request,
    requestModalInfo,
  ]);

  return (
    <div className="">
      <h6>Solicitar Cubrimientos</h6>
      <div
        className={`col-12 ${
          requestModalInfo?.info?.interruptionTypeId ? 'col-md-6' : 'col-md-4'
        } mb-3`}
      >
        <label className="form-label">* Fecha cubrimiento</label>
        <input
          type="date"
          name=""
          className="form-control text-dark"
          id=""
          onChange={(e) => handleInputInterruption('date', e.target.value)}
          value={request?.interruptionRequest.date.split('T')[0]}
          disabled
        />
      </div>
      <FormBlock
        title="Informacion del Cubrimiento"
        showBadge={true}
        badgeText={1}
        badgeVariant="grey"
      >
        <div className="row">
          <div className="col-12 col-md-4">
            <label className="form-label">Solicitante</label>
            <input
              type="text"
              className="form-control text-dark"
              value={requestModalInfo?.info?.employee ?? ''}
              disabled
              readOnly
            />
          </div>
          <div
            className={`col-12 ${
              requestModalInfo?.info?.interruptionTypeId ? 'col-md-6' : 'col-md-4'
            }`}
          >
            <label className="form-label">Turno asignado</label>
            <input
              type="text"
              name=""
              className="form-control text-dark"
              id=""
              disabled={true}
              value={
                // @ts-ignore
                requestModalInfo?.info?.workshiftName || requestModalInfo?.info?.interruptionName
              }
            />
          </div>

          {!requestModalInfo?.info?.interruptionTypeId && (
            <div className="col-12 col-md-4">
              <label className="form-label">Permisos</label>
              <InterruptionAllowedCoverSelector
                name="interruptionTypeRequestId"
                onChange={handlerInputChangeCover}
                value={coverRequest.interruptionTypeRequestId ?? undefined}
              />
            </div>
          )}
        </div>
      </FormBlock>

      <FormBlock
        title="Empleado que Realizará el Cubrimiento"
        showBadge={true}
        badgeText={2}
        badgeVariant="grey"
      >
        <div>
          <div className="row mb-3">
            <CoverPersonSearchField
              onChangeCoverPerson={handlerChangeCoverPerson}
              onChangeCoverRequest={handlerInputChangeCover}
              coverScheduling={coverScheduling}
              employee={employee}
              setEmployees={setEmployees}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">* Turno</label>
            <WorkshiftsLongShiftSelector
              name="workshiftRequestId"
              onChange={handlerInputChangeCover}
              value={coverRequest.workshiftRequestId ?? undefined}
              compensationDate={request?.interruptionRequest.date.split('T')[0]}
              disable={disableWorkshiftsLongShiftSelectorCase2()}
            />
          </div>
        </div>
      </FormBlock>

      <FormBlock
        title="Compensación del Cubrimiento"
        showBadge={true}
        badgeText={3}
        badgeVariant="grey"
      >
        <div>
          <div className="row mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Fecha de compensación</label>
              {requestModalInfo?.info?.interruptionTypeId && (
                <input
                  type="date"
                  name=""
                  className="form-control text-dark"
                  id=""
                  onChange={(e) => handlerInputChangeCover('compensationDate', e.target.value)}
                  value={coverRequest.compensationDate ?? ''}
                />
              )}
              {requestModalInfo?.info?.workshiftId && (
                <input
                  type="date"
                  name=""
                  className="form-control text-dark"
                  id=""
                  onChange={(e) => handlerInputChangeCover('compensationDate', e.target.value)}
                  value={coverRequest.compensationDate ?? ''}
                  disabled={isCompensationDateDisabled}
                />
              )}
            </div>
          </div>
          {isLoading && (
            <div className="row mb-3">
              <div className="col-12 col-md-6 text-muted">Cargando programaciones...</div>
            </div>
          )}

          {coverEmployeeWorkshift && employeeWorkshift && !isLoading && (
            <div className="row mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label">* Turno Solicitante</label>
                <input
                  type="text"
                  className="form-control text-dark"
                  value={employeeWorkshift && getWorkshiftDescription(employeeWorkshift)}
                  readOnly
                  disabled={true}
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">* Turno Cubridor</label>
                <input
                  type="text"
                  className="form-control text-dark"
                  value={coverEmployeeWorkshift && getWorkshiftDescription(coverEmployeeWorkshift)}
                  readOnly
                  disabled={true}
                />
              </div>
            </div>
          )}
          <div className="row mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label">* Turno a compensar</label>
              <WorkshiftsLongShiftSelector
                name="workshiftIdToBeCompensated"
                onChange={handlerInputChangeCover}
                value={coverRequest.workshiftIdToBeCompensated ?? undefined}
                compensationDate={request?.interruptionRequest.date.split('T')[0]}
                disable={disableWorkshiftsLongShiftSelectorCase1()}
              />
            </div>
          </div>
        </div>
      </FormBlock>

      <FormBlock title="Observaciones">
        <div className="row">
          <div className="form-group">
            <label className="form-label" htmlFor="">
              Escribe el motivo del cubrimiento
            </label>

            <textarea
              value={coverRequest.comments}
              onChange={(e) => handlerInputChangeCover('comments', e.target.value)}
              className="form-control text-dark"
              name=""
              id=""
              cols={20}
              rows={5}
            ></textarea>
          </div>
        </div>
      </FormBlock>
    </div>
  );
};
